import React from 'react'
import { createPortal } from 'react-dom'
import { api } from '../api/client'
import { useApp } from '../context/AppContext'
import { Icon } from '../ui/Icon'
import NotificationsSheet from './NotificationsSheet'
import './notifications.css'

// The API stops counting at 10, so 10 is "more than nine".
const shown = (n: number) => (n > 9 ? '9+' : String(n))

// Bell is the header's bell: a badge with how many notifications are new, and
// the list behind a tap. The count is asked for when the app starts, when she
// comes back to it and on every tab change; there is no polling, so the API
// can still idle. A count that can't be fetched leaves the bell as it was and
// says nothing.
export default function Bell() {
  const { state } = useApp()
  const [count, setCount] = React.useState(0)
  const [open, setOpen] = React.useState(false)

  // Each ask gets a number, and only the latest may answer: a slow reply must
  // not bring back a badge she has already cleared.
  const latest = React.useRef(0)
  const refresh = React.useCallback(() => {
    const ask = ++latest.current
    api
      .unreadNotifications()
      .then((n) => {
        if (ask === latest.current) setCount(n)
      })
      .catch(() => {})
  }, [])

  React.useEffect(() => {
    refresh()
  }, [refresh, state.activeTab])

  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      latest.current++
    }
  }, [refresh])

  const seen = React.useCallback(() => {
    latest.current++
    setCount(0)
  }, [])

  const sheet = open ? <NotificationsSheet onClose={() => setOpen(false)} onSeen={seen} /> : null
  const host = typeof document === 'undefined' ? null : document.querySelector('.app-container')

  return (
    <>
      <button
        type="button"
        className="icon-btn icon-btn--filled bell"
        aria-label={count > 0 ? `Notifications, ${shown(count)} new` : 'Notifications'}
        onClick={() => setOpen(true)}
      >
        <Icon name="bell" size={20} />
        {count > 0 && <span className="bell-badge" aria-hidden="true">{shown(count)}</span>}
      </button>
      {sheet && (host ? createPortal(sheet, host) : sheet)}
    </>
  )
}
